var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { Button, Link, HeadingText, BodyText, Dialog, Sheet, CloseIcon } from '@paypalcorp/pp-react';
import PasskeyIcon from './PasskeyIcon';
import PayPalLogo from './PayPalLogo';
import { initializeAsync, initializeSync } from '../common/fn';
import { createPublicKeyCredential, getIsUvpaa, getIsCMA } from '../common/CredentialsAPI/utils';
import { getProp, isMobile, isAndroid } from '../common/utils';
export default ({ authEventHandler, authProviderContext = { fnData: {} }, csrf, csrfNonce, logger, publicCredential, getCreateChallenge, optinOperation, localizedContent, options = {}, }) => {
    const [challengeResponse, setChallengeResponse] = useState(null);
    const [buttonState, setButtonState] = useState('initial');
    const [enrollmentSuccessfulState, setEnrollmentSuccessfulState] = useState(false);
    useEffect(() => {
        authEventHandler({
            event: 'authProviderLoaded',
        });
        logger.track('ap_loaded');
        logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
    }, []);
    useEffect(() => {
        if (enrollmentSuccessfulState) {
            return;
        }
        setButtonState('initial');
    }, [options]);
    useEffect(() => {
        initializeAsync(authProviderContext.fnData);
        initializeSync(authProviderContext.fnData);
        logger.track('ob_passkey_rendered');
        logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
    }, []);
    const handleCreatePasskey = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        logger.track('ob_passkey_continue_clicked');
        logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
        authEventHandler({
            event: 'isPasskeyLoading',
        });
        setButtonState('processing');
        let response;
        if (!challengeResponse) {
            // To avoid downstream API getting abused, we will only trigger the API request once before user leaving the enrollment page
            // We will keep the challenge response within state
            authEventHandler({
                event: 'prebindTriggered',
            });
            const isUvpaaAvailable = yield getIsUvpaa();
            const isCmaAvailable = yield getIsCMA();
            if (!(isUvpaaAvailable && isCmaAvailable)) {
                logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
                if (!isUvpaaAvailable) {
                    logger.track('uvpaa_not_available');
                    logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
                    authEventHandler({
                        event: 'uvpaaNotAvailable',
                    });
                }
                if (!isCmaAvailable) {
                    logger.track('cma_not_available');
                    logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
                    authEventHandler({
                        event: 'cmaNotAvailable',
                    });
                }
                setButtonState('initial');
                return;
            }
            try {
                logger.track('ob_passkey_prebind_triggered');
                response = yield getCreateChallenge({});
                if (response.errors || !getProp(response, 'data.webAuthN.getCreateChallenge.challenge')) {
                    logger.track('ob_passkey_prebind_failed');
                    logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
                    authEventHandler({
                        event: 'challengeNotAvailable',
                    });
                    setButtonState('initial');
                    return;
                }
                logger.track('ob_passkey_prebind_succeeded');
                setChallengeResponse(response);
                authEventHandler({
                    event: 'prebindSuccessful',
                });
            }
            catch (e) {
                logger.track('ob_passkey_prebind_failed');
                logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
                authEventHandler({
                    event: 'challengeNotAvailable',
                });
                return;
            }
        }
        else {
            logger.track('ob_passkey_prebind_challenge_available');
            response = challengeResponse;
            authEventHandler({
                event: 'prebindSuccessful',
            });
        }
        authEventHandler({
            event: 'publicKeyCreateTriggered',
        });
        logger.track('ob_passkey_publickey_create_triggered');
        const publicKey = yield createPublicKeyCredential({
            passkeyChallenge: response,
            publicCredential,
            env: authProviderContext.env,
        });
        if (!publicKey || (publicKey && publicKey.error)) {
            // iOS/Android will constantly consider user cancel the system prompt after first cancellation triggered
            const safariCancelMessage = 'This request has been cancelled by the user.';
            const chromeCancelMessage = 'The operation either timed out or was not allowed.';
            if ((publicKey === null || publicKey === void 0 ? void 0 : publicKey.name) === 'NotAllowedError' &&
                ((publicKey === null || publicKey === void 0 ? void 0 : publicKey.message.includes(safariCancelMessage)) || (publicKey === null || publicKey === void 0 ? void 0 : publicKey.message.includes(chromeCancelMessage)))) {
                // User canceled the system prompt
                logger.track(`ob_passkey_create_canceled|${JSON.stringify({
                    errorName: publicKey === null || publicKey === void 0 ? void 0 : publicKey.name,
                    errorMessage: publicKey === null || publicKey === void 0 ? void 0 : publicKey.message,
                })}`);
                logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
                authEventHandler({
                    event: 'requestCanceled',
                });
                setButtonState('initial');
                return;
            }
            logger.track(`ob_passkey_create_failed|${JSON.stringify({
                errorName: publicKey === null || publicKey === void 0 ? void 0 : publicKey.name,
                errorMessage: publicKey === null || publicKey === void 0 ? void 0 : publicKey.message,
            })}`);
            logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
            authEventHandler({
                event: 'publicKeyCreateFailed',
                data: {
                    errorName: publicKey.name,
                    message: publicKey.message,
                },
            });
            setButtonState('initial');
            return;
        }
        logger.track('ob_passkey_create_succeeded');
        logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
        authEventHandler({
            event: 'publicKeyCreateSuccessful',
        });
        let optinResponse;
        try {
            authEventHandler({
                event: 'publicKeyBindTriggered',
            });
            logger.track('ob_passkey_optin_triggered');
            optinResponse = yield optinOperation(encodeURIComponent(JSON.stringify(publicKey)));
            const optinFailed = optinResponse.errors ||
                !getProp(optinResponse, 'data.webAuthN.optin') ||
                getProp(optinResponse, 'data.webAuthN.optin.result') === 'failed';
            if (!optinFailed) {
                logger.track('ob_passkey_optin_succeeded');
                logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
                setButtonState('success');
                setEnrollmentSuccessfulState(true);
                authEventHandler({
                    event: 'publicKeyBindSuccess',
                });
            }
            else {
                logger.track('ob_passkey_optin_failed');
                logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
                authEventHandler({
                    event: 'publicKeyBindFail',
                });
                setButtonState('initial');
            }
        }
        catch (e) {
            logger.track('ob_passkey_optin_failed');
            logger.flush({ csrf, csrfNonce: csrf ? null : csrfNonce });
            authEventHandler({
                event: 'publicKeyBindFail',
            });
            setButtonState('initial');
        }
        return;
    });
    return (React.createElement("div", { className: "iwc emmbeddedComponent" },
        React.createElement("div", null,
            React.createElement(HeadingText, { size: "sm", className: "heading" }, localizedContent('genericHeading')),
            React.createElement(PasskeyIcon, null),
            React.createElement(BodyText, { className: 'subHeading' },
                React.createElement("span", null, localizedContent('subHeading')))),
        React.createElement("div", { style: { margin: '0 0 24px 0' } },
            React.createElement(Button, { onClick: handleCreatePasskey, style: { width: '191px', height: '48px' }, className: "continueBtn", "data-testid": "button-continue", id: "button-continue", btnState: buttonState }, localizedContent('addPasskeyMobile')))));
};
